import { useState, useMemo } from 'react';
import { Tag, AlertTriangle } from 'lucide-react';
import { fmtUSD } from '../../utils/formatters';
import { getTradeNetPnL } from '../../config';
import { getExecutionInstantMs } from '../../services/timeService';
import GlassSelect from '../Common/GlassSelect';

const TRADES_PER_PAGE = 100;

// Shared frosted-glass pagination button class — matches the v7.5.15
// glass aesthetic (translucent + border + soft hover lift). Both the
// top and bottom pagination strips reuse this.
const pageBtnClass = (darkMode) =>
  `px-3 py-1 rounded-md text-sm border transition-[background-color,border-color,box-shadow,color] duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${
    darkMode
      ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08] hover:border-white/20 hover:text-white'
      : 'bg-white/60 border-gray-200 text-gray-700 hover:bg-white/80 hover:border-gray-300 hover:text-gray-900'
  }`;

/**
 * TradesTab — Trade History table with pagination and tag filtering.
 * Extracted from TradingJournal.js (Phase 3).
 */
const TradesTab = ({
  darkMode,
  filteredTrades,
  accounts,
  trades,
  getAccountById,
  formatTradeTime,
  deleteTrade,
  onEditTrade,
}) => {
  // Local state (view-only, not persisted)
  const [tradesPage, setTradesPage] = useState(1);
  const [selectedTagFilter, setSelectedTagFilter] = useState('all');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Get all unique tags from trades for the filter dropdown
  const allUniqueTags = useMemo(() => {
    const tagSet = new Set();
    trades.forEach(trade => {
      if (trade.tags && trade.tags.length > 0) {
        trade.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [trades]);

  // Wall-clock execution timestamp accounting for the 6pm-ET trading day
  // boundary. Sourced from timeService — single implementation app-wide.
  const getExecutionTimestamp = (trade) => getExecutionInstantMs(trade.date, trade.entryTime);

  // Apply tag filter for trades tab and sort by execution time (newest first)
  const tradesTabFiltered = useMemo(() => {
    return (selectedTagFilter === 'all'
      ? filteredTrades
      : filteredTrades.filter(t => t.tags && t.tags.includes(selectedTagFilter))
    ).sort((a, b) => {
      return getExecutionTimestamp(b) - getExecutionTimestamp(a);
    });
  }, [filteredTrades, selectedTagFilter]);

  return (
    <div className={`rounded-lg shadow-md p-6 transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h2 className={`text-xl font-bold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Trade History</h2>
          {/* Tag Filter Dropdown */}
          {allUniqueTags.length > 0 && (
            <div className="flex items-center gap-2">
              <Tag size={16} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
              <GlassSelect
                value={selectedTagFilter}
                onChange={(v) => { setSelectedTagFilter(v); setTradesPage(1); }}
                options={[
                  { value: 'all', label: 'All Tags' },
                  ...allUniqueTags.map(tag => ({ value: tag, label: tag })),
                ]}
                darkMode={darkMode}
                wrapperClassName="relative inline-block"
                className="w-44"
              />
              {selectedTagFilter !== 'all' && (
                <button
                  onClick={() => { setSelectedTagFilter('all'); setTradesPage(1); }}
                  className={`text-xs px-2 py-1 rounded transition-colors ${
                    darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {tradesTabFiltered.length}{selectedTagFilter !== 'all' ? ` (filtered)` : ''} of {filteredTrades.length} trades
        </span>
      </div>
      {tradesTabFiltered.length === 0 ? (
        <div className={`text-center py-8 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {selectedTagFilter !== 'all'
            ? `No trades found with tag "${selectedTagFilter}". Try a different filter.`
            : 'No trades found. Click "Add Trade" to get started!'}
        </div>
      ) : (
        <>
          {/* Pagination Controls - Top */}
          {tradesTabFiltered.length > TRADES_PER_PAGE && (
            <div className={`flex items-center justify-between mb-4 pb-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Showing {((tradesPage - 1) * TRADES_PER_PAGE) + 1} - {Math.min(tradesPage * TRADES_PER_PAGE, tradesTabFiltered.length)} of {tradesTabFiltered.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setTradesPage(1)}
                  disabled={tradesPage === 1}
                  className={pageBtnClass(darkMode)}
                >
                  First
                </button>
                <button
                  onClick={() => setTradesPage(p => Math.max(1, p - 1))}
                  disabled={tradesPage === 1}
                  className={pageBtnClass(darkMode)}
                >
                  ← Prev
                </button>
                <span className={`px-3 py-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Page {tradesPage} of {Math.ceil(tradesTabFiltered.length / TRADES_PER_PAGE)}
                </span>
                <button
                  onClick={() => setTradesPage(p => Math.min(Math.ceil(tradesTabFiltered.length / TRADES_PER_PAGE), p + 1))}
                  disabled={tradesPage >= Math.ceil(tradesTabFiltered.length / TRADES_PER_PAGE)}
                  className={pageBtnClass(darkMode)}
                >
                  Next →
                </button>
                <button
                  onClick={() => setTradesPage(Math.ceil(tradesTabFiltered.length / TRADES_PER_PAGE))}
                  disabled={tradesPage >= Math.ceil(tradesTabFiltered.length / TRADES_PER_PAGE)}
                  className={pageBtnClass(darkMode)}
                >
                  Last
                </button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`border-b transition-colors duration-300 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Date</th>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Time</th>
                {accounts.length > 0 && (
                  <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Account</th>
                )}
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Symbol</th>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Dir</th>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Setup</th>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>ATM</th>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Entry</th>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Exits</th>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Stop Loss</th>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>P&L</th>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>R Multiple</th>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Tags</th>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Mistakes</th>
                <th className={`text-center py-2 px-3 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tradesTabFiltered.slice((tradesPage - 1) * TRADES_PER_PAGE, tradesPage * TRADES_PER_PAGE).map(trade => {
                // Calculate net P&L for this trade
                const tradeAccount = accounts.find(acc => acc.id === trade.accountId);
                const { netPnl } = getTradeNetPnL(trade, tradeAccount);

                return (
                <tr key={trade.id} className={`border-b transition-colors duration-300 ${
                  darkMode
                    ? 'border-gray-700 hover:bg-gray-700'
                    : 'border-gray-100 hover:bg-gray-50'
                }`}>
                  <td className={`py-2 px-3 text-center transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{trade.date}</td>
                  <td className={`py-2 px-3 text-center transition-colors duration-300 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {trade.entryTime || trade.exitTime ? (
                      <span>
                        {formatTradeTime(trade.entryTime) || ''}
                        {trade.entryTime && trade.exitTime && ' → '}
                        {formatTradeTime(trade.exitTime) || ''}
                      </span>
                    ) : '-'}
                  </td>
                  {accounts.length > 0 && (
                    <td className={`py-2 px-3 text-center transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {trade.accountId ? (
                        <span className={`text-xs px-2 py-1 rounded ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          {getAccountById(trade.accountId)?.name || trade.accountId}
                        </span>
                      ) : '-'}
                    </td>
                  )}
                  <td className={`py-2 px-3 text-center font-medium transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="flex items-center justify-center gap-1">
                      {trade.symbol}
                      {trade.dayBoundaryClosed && (
                        <span title="Position held past 5pm ET - force closed at day boundary">
                          <AlertTriangle className="w-4 h-4 text-orange-500" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      trade.direction?.toLowerCase() === 'long'
                        ? 'bg-green-500/20 text-green-500'
                        : trade.direction?.toLowerCase() === 'short'
                          ? 'bg-red-500/20 text-red-500'
                          : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {trade.direction || '-'}
                    </span>
                  </td>
                  <td className={`py-2 px-3 text-center transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{trade.setup}</td>
                  <td className={`py-2 px-3 text-center transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {trade.atmStrategy ? (
                      <span className={`text-xs px-2 py-1 rounded ${
                        darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {trade.atmStrategy}
                      </span>
                    ) : '-'}
                  </td>
                  <td className={`py-2 px-3 text-center transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>${trade.entry}</td>
                  <td className={`py-2 px-3 text-center transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {/* Handle both new exits array and legacy single exit */}
                    {trade.exits && trade.exits.length > 0 ? (
                      <div className="text-xs">
                        {trade.exits.map((exit, idx) => (
                          <div key={idx}>
                            {exit.quantity}@${exit.price}
                          </div>
                        ))}
                      </div>
                    ) : trade.exit ? (
                      `$${Number(trade.exit).toFixed(2)}`
                    ) : '-'}
                  </td>
                  <td className={`py-2 px-3 text-center transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {trade.stopLoss ? `$${trade.stopLoss.toFixed(2)}` : '-'}
                  </td>
                  <td className={`py-2 px-3 text-center font-medium ${netPnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {netPnl >= 0 ? '' : '-'}${fmtUSD(netPnl)}
                  </td>
                  <td className={`py-2 px-3 text-center ${trade.rMultiple >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {trade.rMultiple ? `${trade.rMultiple}R` : '-'}
                  </td>
                  <td className="py-2 px-3 text-center">
                    {trade.tags && trade.tags.map(tag => (
                      <span key={tag} className={`px-2 py-1 rounded-full text-xs mr-1 mb-1 inline-block transition-colors duration-300 ${
                        darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {tag}
                      </span>
                    ))}
                  </td>
                  <td className="py-2 px-3 text-center">
                    {trade.mistakes && trade.mistakes.map(mistake => (
                      <span key={mistake} className={`px-2 py-1 rounded-full text-xs mr-1 mb-1 inline-block transition-colors duration-300 ${
                        darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800'
                      }`}>
                        {mistake}
                      </span>
                    ))}
                  </td>
                  <td className="py-2 px-3 text-center">
                    {deleteConfirmId === trade.id ? (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => deleteTrade(trade.id)}
                          className="px-2 py-1 text-xs bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            darkMode
                              ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => onEditTrade(trade)}
                          className={`p-1 rounded transition-colors ${
                            darkMode
                              ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700'
                              : 'text-gray-400 hover:text-blue-600 hover:bg-gray-100'
                          }`}
                          title="Edit trade"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(trade.id)}
                          className={`p-1 rounded transition-colors ${
                            darkMode
                              ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700'
                              : 'text-gray-400 hover:text-red-600 hover:bg-gray-100'
                          }`}
                          title="Delete trade"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
          {/* Pagination Controls - Bottom */}
          {tradesTabFiltered.length > TRADES_PER_PAGE && (
            <div className={`flex items-center justify-between mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Showing {((tradesPage - 1) * TRADES_PER_PAGE) + 1} - {Math.min(tradesPage * TRADES_PER_PAGE, tradesTabFiltered.length)} of {tradesTabFiltered.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setTradesPage(1)}
                  disabled={tradesPage === 1}
                  className={pageBtnClass(darkMode)}
                >
                  First
                </button>
                <button
                  onClick={() => setTradesPage(p => Math.max(1, p - 1))}
                  disabled={tradesPage === 1}
                  className={pageBtnClass(darkMode)}
                >
                  ← Prev
                </button>
                <span className={`px-3 py-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Page {tradesPage} of {Math.ceil(tradesTabFiltered.length / TRADES_PER_PAGE)}
                </span>
                <button
                  onClick={() => setTradesPage(p => Math.min(Math.ceil(tradesTabFiltered.length / TRADES_PER_PAGE), p + 1))}
                  disabled={tradesPage >= Math.ceil(tradesTabFiltered.length / TRADES_PER_PAGE)}
                  className={pageBtnClass(darkMode)}
                >
                  Next →
                </button>
                <button
                  onClick={() => setTradesPage(Math.ceil(tradesTabFiltered.length / TRADES_PER_PAGE))}
                  disabled={tradesPage >= Math.ceil(tradesTabFiltered.length / TRADES_PER_PAGE)}
                  className={pageBtnClass(darkMode)}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TradesTab;
